import { useState, useEffect, useCallback } from 'react';

/**
 * A custom hook that defers the value update until after a specified delay.
 * This is useful for optimizing performance by reducing the number of re-renders
 * when the value changes frequently.
 *
 * @param value - The value to be deferred.
 * @param delay - The delay in milliseconds before updating the value.
 * @returns The deferred value.
 */
export const useDeferredValueWithCustomDebounceTime = <T>(
  value: T,
  delay = 500,
) => {
  const [deferredValue, setDeferredValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDeferredValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return deferredValue;
};

/**
 * Custom hook to manage focus state of an input field.
 *
 * @returns A tuple containing the focused state and actions to manipulate it.
 */
export const useFocusState = (): [FocusedState, FocusStateActions] => {
  const [focusedState, setFocusedState] = useState<FocusedState>({
    field: null,
    value: null,
  });

  const setFieldFocus = useCallback((field: string, value: number) => {
    setFocusedState({ field, value });
  }, []);

  const clearFocus = useCallback(() => {
    setFocusedState({ field: null, value: null });
  }, []);

  const updateFocusedValue = useCallback((value: number | null) => {
    setFocusedState((prev) =>
      prev.field === null ? prev : { ...prev, value },
    );
  }, []);

  const getFieldValue = useCallback(
    (defaultValue: number, fieldId: string): number | '' => {
      if (focusedState.field === fieldId) {
        if (defaultValue === 0 && focusedState.value === null) return '';
        return focusedState.value ?? defaultValue;
      }
      return defaultValue;
    },
    [focusedState],
  );

  return [
    focusedState,
    {
      setFieldFocus,
      clearFocus,
      updateFocusedValue,
      getFieldValue,
    },
  ];
};

// Types
interface FocusedState {
  field: string | null;
  value: number | null;
}

interface FocusStateActions {
  setFieldFocus: (field: string, value: number) => void;
  clearFocus: () => void;
  updateFocusedValue: (value: number | null) => void;
  getFieldValue: (defaultValue: number, fieldId: string) => number | '';
}
